import type Phaser from "phaser";
import { explosionRing, spark } from "@yjsgames/phaser-fx";
import {
  computeBrickImpactNormal,
  computeBrickImpactPoint,
  computeBrickImpactScale,
  darkenTint,
  type BrickImpactBounds,
} from "./brickImpactFx.js";
import { DEPTH } from "./depthLayers.js";
import type { FxImagePool } from "./fxImagePool.js";
import { PALETTE_NUM } from "../themes/palette.js";

const HIT_PARTICLE_TEXTURE = "hit_spark_white";

export interface EnemyShipHitFxOptions extends BrickImpactBounds {
  readonly tint: number;
  readonly isCrit?: boolean;
  readonly damageRatio?: number;
  readonly rng?: () => number;
}

export interface EnemyShipHitSparkSpawn {
  readonly texture: string;
  readonly tint: number;
  readonly x: number;
  readonly y: number;
  readonly toX: number;
  readonly toY: number;
  readonly scale: number;
  readonly endScale: number;
  readonly alpha: number;
  readonly rotation: number;
  readonly duration: number;
}

export interface EnemyShipHitPlan {
  readonly point: { readonly x: number; readonly y: number };
  readonly normal: { readonly x: number; readonly y: number };
  readonly impactScale: number;
  readonly color: number;
  readonly ringRadius: number;
  readonly ringEndScale: number;
  readonly flashStartScale: number;
  readonly flashEndScale: number;
  readonly sparks: readonly EnemyShipHitSparkSpawn[];
}

export function computeEnemyShipHitPlan(opts: EnemyShipHitFxOptions): EnemyShipHitPlan {
  const rng = opts.rng ?? Math.random;
  const point = computeBrickImpactPoint(opts);
  const normal = computeBrickImpactNormal(opts);
  const impactScale = computeBrickImpactScale(opts.damageRatio);
  const color = opts.isCrit ? PALETTE_NUM.damageText : darkenTint(opts.tint, 0.86);
  const startX = point.x + normal.x * 2;
  const startY = point.y + normal.y * 2;
  const tangent = { x: -normal.y, y: normal.x };
  const sparkCount = opts.isCrit ? 5 : 3;
  const sparks: EnemyShipHitSparkSpawn[] = [];

  for (let i = 0; i < sparkCount; i += 1) {
    const spread = (rng() - 0.5) * (opts.isCrit ? 1.05 : 0.82);
    const dirX = normal.x + tangent.x * spread;
    const dirY = normal.y + tangent.y * spread;
    const len = Math.max(0.001, Math.hypot(dirX, dirY));
    const nx = dirX / len;
    const ny = dirY / len;
    const travel = (opts.isCrit ? 17 : 12) * impactScale * (0.68 + rng() * 0.42);
    const scale = (opts.isCrit ? 0.42 : 0.31) * impactScale * (0.78 + rng() * 0.38);
    sparks.push({
      texture: HIT_PARTICLE_TEXTURE,
      tint: i === 0 && opts.isCrit ? PALETTE_NUM.accentGold : color,
      x: startX,
      y: startY,
      toX: startX + nx * travel,
      toY: startY + ny * travel + rng() * 3,
      scale,
      endScale: scale * 0.32,
      alpha: opts.isCrit ? 0.9 : 0.72,
      rotation: Math.atan2(ny, nx) + (rng() - 0.5) * 0.6,
      duration: (opts.isCrit ? 210 : 170) * (0.88 + rng() * 0.26),
    });
  }

  return {
    point,
    normal,
    impactScale,
    color,
    ringRadius: (opts.isCrit ? 5.8 : 4.4) * impactScale,
    ringEndScale: opts.isCrit ? 1.45 : 1.25,
    flashStartScale: (opts.isCrit ? 0.24 : 0.18) * impactScale,
    flashEndScale: (opts.isCrit ? 0.62 : 0.45) * impactScale,
    sparks,
  };
}

export function emitEnemyShipHitFx(
  scene: Phaser.Scene,
  pool: FxImagePool,
  opts: EnemyShipHitFxOptions,
): readonly Phaser.GameObjects.GameObject[] {
  const plan = computeEnemyShipHitPlan(opts);
  const out: Phaser.GameObjects.GameObject[] = [];

  out.push(
    explosionRing(scene, plan.point.x + plan.normal.x * 2, plan.point.y + plan.normal.y * 2, plan.ringRadius, {
      color: plan.color,
      alpha: opts.isCrit ? 0.42 : 0.3,
      endScale: plan.ringEndScale,
      duration: opts.isCrit ? 190 : 150,
      depth: DEPTH.projectile + 0.25,
    }),
  );
  out.push(
    spark(scene, plan.point.x + plan.normal.x * 2, plan.point.y + plan.normal.y * 2, HIT_PARTICLE_TEXTURE, {
      startScale: plan.flashStartScale,
      endScale: plan.flashEndScale,
      alpha: opts.isCrit ? 0.82 : 0.64,
      duration: opts.isCrit ? 180 : 140,
      tint: plan.color,
      depth: DEPTH.projectile + 0.35,
    }),
  );

  for (const s of plan.sparks) {
    const img = pool
      .acquire()
      .setTexture(s.texture)
      .setPosition(s.x, s.y)
      .setDepth(DEPTH.projectile + 0.4)
      .setTint(s.tint)
      .setBlendMode("ADD")
      .setAlpha(s.alpha)
      .setScale(s.scale)
      .setRotation(s.rotation)
      .setActive(true)
      .setVisible(true);
    scene.tweens.add({
      targets: img,
      x: s.toX,
      y: s.toY,
      alpha: 0,
      scale: s.endScale,
      duration: s.duration,
      ease: "Quad.easeOut",
      onComplete: () => pool.release(img),
    });
    out.push(img);
  }

  return out;
}
